import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, Loader2, Plus, RefreshCw, Send, Trash2 } from "lucide-react";
import { useState } from "react";
import type { ReactNode } from "react";
import { useI18n, useT } from "../../../shared/i18n/i18n";
import { formatRelativeTime } from "../../../shared/lib/format-relative-time";
import { useTRPC } from "../../../shared/api/trpc";
import { cn } from "../../../shared/lib/cn";
import { allWebhookEvents, webhookEventGroups } from "./webhook-events";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

type Endpoint = { eventTypes: string[]; id: string; isActive: boolean; name: string; url: string };

// Webhooks are the most technical thing in the product, so the screen carries
// its own explanation: what it does, what arrives, and how the other side can
// tell the message really came from here.
export function WebhooksSection({ copyable: Copyable }: { copyable: (props: { label?: string; value: string }) => ReactNode }) {
  const t = useT();
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const endpointsQuery = useQuery(trpc.webhooks.list.queryOptions());
  const archiveMutation = useMutation(trpc.webhooks.archive.mutationOptions());
  const [editing, setEditing] = useState<Endpoint | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [issued, setIssued] = useState<{ name: string; secret: string } | null>(null);
  const [openDeliveries, setOpenDeliveries] = useState<string | null>(null);

  const endpoints = (endpointsQuery.data ?? []) as Endpoint[];

  async function refresh() {
    await queryClient.invalidateQueries({ queryKey: trpc.webhooks.list.queryKey() });
  }

  return (
    <section className="flex min-w-0 flex-col gap-3">
      <header className="flex items-start justify-between gap-4">
        <div className="flex min-w-0 flex-col gap-0.5">
          <h2 className="text-sm font-medium">{t("webhooks.title")}</h2>
          <p className="text-xs text-muted-foreground">{t("webhooks.whatItIs")}</p>
        </div>
        <Button onClick={() => setIsCreateOpen(true)} size="sm" variant="outline">
          <Plus data-icon="inline-start" />
          {t("webhooks.add")}
        </Button>
      </header>

      {issued ? (
        <div className="flex min-w-0 flex-col gap-2 rounded-md border border-primary/30 bg-primary/5 px-3 py-2.5">
          <p className="text-xs">{t("webhooks.created", { name: issued.name })}</p>
          <Copyable label={t("webhooks.secret")} value={issued.secret} />
          <p className="text-xs text-muted-foreground">{t("webhooks.secretHint")}</p>
        </div>
      ) : null}

      {endpointsQuery.isLoading ? (
        <p className="text-xs text-muted-foreground">{t("common.loading")}</p>
      ) : endpoints.length === 0 ? (
        <p className="text-xs text-muted-foreground">{t("webhooks.empty")}</p>
      ) : (
        <div className="flex flex-col">
          {endpoints.map((endpoint) => (
            <div className="flex min-w-0 flex-col gap-1 border-b border-border/40 py-2 last:border-0" key={endpoint.id}>
              <div className="flex items-center gap-3">
                <div className="flex min-w-0 flex-1 flex-col">
                  <span className="flex items-center gap-2 truncate text-sm">
                    {endpoint.name}
                    {endpoint.isActive ? null : (
                      <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                        {t("webhooks.paused")}
                      </span>
                    )}
                  </span>
                  <span className="truncate text-xs text-muted-foreground">
                    {endpoint.url} ·{" "}
                    {endpoint.eventTypes.length === 0
                      ? t("webhooks.allEvents")
                      : endpoint.eventTypes.map((event) => t(`webhooks.event.${event}`)).join(", ")}
                  </span>
                </div>
                <Button
                  className="h-7 shrink-0 text-xs"
                  onClick={() => setOpenDeliveries(openDeliveries === endpoint.id ? null : endpoint.id)}
                  size="sm"
                  variant="ghost"
                >
                  <ChevronDown className={cn("transition-transform", openDeliveries === endpoint.id && "rotate-180")} />
                  {t("webhooks.deliveries")}
                </Button>
                <Button className="h-7 shrink-0 text-xs" onClick={() => setEditing(endpoint)} size="sm" variant="ghost">
                  {t("common.edit")}
                </Button>
                <Button
                  className="h-7 shrink-0 text-xs"
                  disabled={archiveMutation.isPending}
                  onClick={async () => {
                    await archiveMutation.mutateAsync({ endpointId: endpoint.id });
                    await refresh();
                  }}
                  size="sm"
                  variant="ghost"
                >
                  <Trash2 data-icon="inline-start" />
                  {t("webhooks.archive")}
                </Button>
              </div>
              {openDeliveries === endpoint.id ? <Deliveries endpointId={endpoint.id} /> : null}
            </div>
          ))}
        </div>
      )}

      <EndpointDialog
        endpoint={editing}
        onDone={async (created) => {
          if (created) setIssued(created);
          setEditing(null);
          setIsCreateOpen(false);
          await refresh();
        }}
        onOpenChange={(next) => {
          if (!next) {
            setEditing(null);
            setIsCreateOpen(false);
          }
        }}
        open={isCreateOpen || editing !== null}
      />
    </section>
  );
}

// Every attempt, with what the other side answered. A webhook that silently
// stopped working is the usual failure, so the history is one click away rather
// than behind a support request.
function Deliveries({ endpointId }: { endpointId: string }) {
  const t = useT();
  const { locale } = useI18n();
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const deliveriesQuery = useQuery(trpc.webhooks.deliveries.queryOptions({ endpointId, limit: 10 }));
  const replayMutation = useMutation(trpc.webhooks.replay.mutationOptions());
  const deliveries = deliveriesQuery.data ?? [];

  if (deliveriesQuery.isLoading) return <p className="pl-1 text-xs text-muted-foreground">{t("common.loading")}</p>;
  if (deliveries.length === 0) return <p className="pl-1 text-xs text-muted-foreground">{t("webhooks.deliveriesEmpty")}</p>;

  return (
    <div className="flex flex-col gap-1 rounded-md bg-muted/30 px-3 py-2">
      {deliveries.map((delivery) => (
        <div className="flex items-center gap-2 text-xs" key={delivery.id}>
          <span
            className={cn(
              "size-1.5 shrink-0 rounded-full",
              delivery.status === "delivered" ? "bg-emerald-500" : delivery.status === "failed" ? "bg-destructive" : "bg-amber-500",
            )}
          />
          <span className="shrink-0 text-muted-foreground">
            {delivery.status === "delivered"
              ? t("webhooks.delivered")
              : delivery.status === "failed"
                ? t("webhooks.failed")
                : t("webhooks.pending")}
          </span>
          <span className="min-w-0 flex-1 truncate text-muted-foreground/80">
            {delivery.lastResponseStatus ? `HTTP ${delivery.lastResponseStatus}` : ""}
            {delivery.lastError ? ` · ${delivery.lastError}` : ""}
          </span>
          <span className="shrink-0 text-muted-foreground/70">{formatRelativeTime(delivery.createdAt, t, locale)}</span>
          <Button
            className="h-6 shrink-0 text-xs"
            disabled={replayMutation.isPending}
            onClick={async () => {
              await replayMutation.mutateAsync({ deliveryId: delivery.id });
              await queryClient.invalidateQueries({ queryKey: trpc.webhooks.deliveries.queryKey({ endpointId, limit: 10 }) });
            }}
            size="sm"
            variant="ghost"
          >
            <RefreshCw data-icon="inline-start" />
            {t("webhooks.replay")}
          </Button>
        </div>
      ))}
    </div>
  );
}

function EndpointDialog({
  endpoint,
  onDone,
  onOpenChange,
  open,
}: {
  endpoint: Endpoint | null;
  onDone: (created: { name: string; secret: string } | null) => Promise<void> | void;
  onOpenChange: (open: boolean) => void;
  open: boolean;
}) {
  const t = useT();
  const trpc = useTRPC();
  const createMutation = useMutation(trpc.webhooks.create.mutationOptions());
  const updateMutation = useMutation(trpc.webhooks.update.mutationOptions());
  const [name, setName] = useState(endpoint?.name ?? "");
  const [url, setUrl] = useState(endpoint?.url ?? "");
  const [events, setEvents] = useState<string[]>(endpoint?.eventTypes ?? []);
  const [isActive, setIsActive] = useState(endpoint?.isActive ?? true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(endpoint?.id ?? null);

  // Re-seed when a different endpoint is opened for editing. Both sides are
  // normalised to null: `endpoint?.id` is undefined for a new destination, and
  // comparing that against null re-seeds on every render.
  const openedId = endpoint?.id ?? null;
  if (openedId !== editingId) {
    setEditingId(openedId);
    setName(endpoint?.name ?? "");
    setUrl(endpoint?.url ?? "");
    setEvents(endpoint?.eventTypes ?? []);
    setIsActive(endpoint?.isActive ?? true);
    setError(null);
  }

  async function submit() {
    setError(null);
    try {
      if (endpoint) {
        await updateMutation.mutateAsync({ endpointId: endpoint.id, eventTypes: events, isActive, name: name.trim(), url: url.trim() });
        await onDone(null);
        return;
      }
      const created = await createMutation.mutateAsync({ eventTypes: events, name: name.trim(), url: url.trim() });
      await onDone({ name: created.name, secret: created.secret });
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "Could not save this destination.");
    }
  }

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className="flex max-h-[min(680px,calc(100dvh-2rem))] min-w-0 flex-col sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{endpoint ? endpoint.name : t("webhooks.add")}</DialogTitle>
          <DialogDescription>{t("webhooks.whatItIs")}</DialogDescription>
        </DialogHeader>

        <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-4 overflow-y-auto">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="webhook-name">{t("webhooks.name")}</Label>
            <Input id="webhook-name" onChange={(event) => setName(event.target.value)} placeholder={t("webhooks.namePlaceholder")} value={name} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="webhook-url">{t("webhooks.url")}</Label>
            <Input id="webhook-url" onChange={(event) => setUrl(event.target.value)} placeholder={t("webhooks.urlPlaceholder")} value={url} />
          </div>

          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium">{t("webhooks.groupChoose")}</span>
            <p className="text-xs text-muted-foreground">{t("webhooks.groupChooseHint")}</p>
            {webhookEventGroups.map((group) => (
              <div className="flex flex-col gap-1.5" key={group.key}>
                <span className="text-xs font-medium text-muted-foreground">{t(`webhooks.events${capitalize(group.key)}`)}</span>
                <div className="flex flex-wrap gap-1.5">
                  {group.events.map((event) => {
                    const isOn = events.includes(event);
                    return (
                      <button
                        aria-pressed={isOn}
                        className={cn(
                          "rounded-md border px-2 py-1 text-xs transition-colors",
                          isOn ? "border-primary/60 bg-primary/10" : "border-border/60 hover:bg-accent/40",
                        )}
                        key={event}
                        onClick={() => setEvents((current) => (isOn ? current.filter((entry) => entry !== event) : [...current, event]))}
                        type="button"
                      >
                        {t(`webhooks.event.${event}`)}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {endpoint ? (
            <label className="flex items-start gap-2.5">
              <Checkbox checked={isActive} className="mt-0.5" onCheckedChange={(checked: boolean) => setIsActive(Boolean(checked))} />
              <span className="flex min-w-0 flex-col">
                <span className="text-sm">{isActive ? t("webhooks.active") : t("webhooks.paused")}</span>
                <span className="text-xs text-muted-foreground">{t("webhooks.pausedHint")}</span>
              </span>
            </label>
          ) : null}

          {error ? <p className="text-xs text-destructive">{error}</p> : null}
        </div>

        <DialogFooter>
          <Button onClick={() => onOpenChange(false)} type="button" variant="ghost">
            {t("common.cancel")}
          </Button>
          <Button disabled={isPending || name.trim().length === 0 || url.trim().length < 8} onClick={() => void submit()}>
            {isPending ? <Loader2 className="animate-spin" data-icon="inline-start" /> : <Send data-icon="inline-start" />}
            {t("webhooks.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

export { allWebhookEvents };
